"use client"

import { ChevronDown, ChevronUp, User } from "lucide-react"
import { cn } from "@/core/lib/utils"
import { CommentItem } from "@/core/types/comments"
import { useState } from "react"
import { useDictionary } from "@/core/hooks/use-dictionary"

interface CommentListProps {
  comments: CommentItem[]
}

export default function CommentList({ comments }: CommentListProps) {
  const { dictionary } = useDictionary()
  const [showAll, setShowAll] = useState(false)
  const visibleComments = showAll ? comments : comments.slice(0, 3)
  const hasMore = comments.length > 3

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className="flex flex-col">


      {/* Comments */}
      <div className="space-y-6">
        {visibleComments.map((comment) => (
          <div key={comment.id} className="group flex gap-4">
            {/* Avatar Column */}
            <div className="flex flex-col items-center gap-2">
              <div className="relative">
                <div className="w-9 h-9 rounded-full bg-secondary border border-border flex items-center justify-center overflow-hidden">
                  <User className="w-4 h-4 text-primary-600" strokeWidth={1.5} />
                </div>

              </div>
              {/* Thread line */}
              <div className="w-px flex-1 bg-border/50 group-last:hidden" />
            </div>

            {/* Content */}
            <div className="flex-1 pb-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-baseline gap-2">
                  <span className="text-sm font-semibold text-primary-900 cursor-pointer">
                    {comment.author_name}
                  </span>
                  <span className="text-xs text-primary-600">
                    {comment.created_at_jalali ?? formatDate(comment.created_at)}
                  </span>
                </div>

              </div>

              {/* Subject */}
              {comment.subject && (
                <h4 className="text-sm font-semibold text-primary-900 mb-1.5">
                  {comment.subject}
                </h4>
              )}

              {/* Content */}
              <div className="text-sm text-primary-600 leading-6 whitespace-pre-wrap">
                {comment.content}
              </div>
            </div>
          </div>
        ))}
      </div>

      {hasMore && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="mt-4 flex items-center gap-2 text-sm text-primary-600 hover:text-primary-900 transition-colors group self-start"
        >
          <div className={cn(
            "h-px bg-border transition-all",
            showAll ? "w-8" : "w-6 group-hover:w-8"
          )} />
          <span className="font-medium">
            {showAll
              ? dictionary.ui.pages.showLess
              : dictionary.ui.pages.showMore
            }
          </span>
          {showAll ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </button>
      )}
    </div>
  )
}